import { useTranslation } from "react-i18next";
import { useFormContext, useWatch } from "react-hook-form";

import { lineItemId, safeLineItemKey } from "./useWithdrawalWizard.js";
import { formatMoney } from "./utils/currency.js";

const inputClass =
  "wp:block wp:w-full wp:appearance-none wp:rounded-md wp:border wp:border-solid wp:border-gray-300 wp:[background-color:var(--wf-input-bg,#ffffff)] wp:px-4 wp:py-3 wp:text-lg wp:[color:var(--wf-text-color,#111827)] wp:shadow-sm wp:focus:border-gray-900 wp:focus:outline-none wp:focus:ring-1 wp:focus:ring-gray-900";

function itemPrice(item) {
  return (
    item.price ||
    item.discounted_unit_price_set?.shop_money?.amount ||
    item.discountedUnitPriceSet?.shopMoney?.amount ||
    ""
  );
}

function availableQuantityFor(item, availability) {
  return availability.available_quantity ?? Number(item.quantity || 0);
}

function lineItemImage(item) {
  const image = item.image || {};

  return {
    url:
      image.url ||
      image.src ||
      item.image_url ||
      item.imageUrl ||
      item.variant?.image?.url ||
      item.product?.featuredImage?.url ||
      "",
    alt: image.altText || image.alt_text || item.title || "",
  };
}

export function isLineItemAvailable(item, availability) {
  return availability.available !== false && availableQuantityFor(item, availability) > 0;
}

export function OrderLineItem({ item, availability, withdrawalReasons }) {
  const { t } = useTranslation();
  const { control, register } = useFormContext();
  const id = lineItemId(item);
  const fieldKey = safeLineItemKey(id);
  const safeId = id.replace(/[^a-zA-Z0-9_-]/g, "_");
  const availableQuantity = availableQuantityFor(item, availability);
  const available = isLineItemAvailable(item, availability);
  const image = lineItemImage(item);
  const checked = Boolean(useWatch({ control, name: `line_item_selected.${fieldKey}` }));
  const selectedReasonId = useWatch({ control, name: `line_item_reason_ids.${fieldKey}` });
  const selectedReason = withdrawalReasons.find((candidate) => String(candidate.id) === String(selectedReasonId));
  const currency = {
    unit: t("proxy.withdrawals.common.currency.unit"),
    separator: t("proxy.withdrawals.common.currency.separator"),
    delimiter: t("proxy.withdrawals.common.currency.delimiter"),
  };

  return (
    <li className="wp:rounded-md wp:border wp:border-solid wp:border-gray-200 wp:[background-color:var(--wf-bg-color,#ffffff)] wp:p-4 wp:shadow-sm">
      <label className="wp:flex wp:items-start wp:gap-3 wp:text-base wp:font-medium wp:[color:var(--wf-text-color,#111827)]">
        <input
          type="checkbox"
          disabled={!available}
          {...register(`line_item_selected.${fieldKey}`)}
          className="wp:mt-6"
        />
        {image.url ? (
          <img
            src={image.url}
            alt={image.alt}
            loading="lazy"
            className="wp:h-16 wp:w-16 wp:flex-none wp:rounded-md wp:border wp:border-solid wp:border-gray-200 wp:bg-gray-50 wp:object-cover"
          />
        ) : null}
        <span>
          <span className="wp:block wp:text-lg">{item.title}</span>
          {item.variant_title || item.variantTitle ? (
            <span className="wp:block wp:text-gray-500">{item.variant_title || item.variantTitle}</span>
          ) : null}
          <span className="wp:block wp:text-gray-500">
            {t("proxy.withdrawals.order.item_quantity", {
              quantity: item.quantity,
              price: formatMoney(itemPrice(item), currency),
            })}
          </span>
        </span>
      </label>

      {!available ? (
        <p className="wp:mt-4 wp:rounded-md wp:border wp:border-solid wp:border-amber-200 wp:bg-amber-50 wp:p-3 wp:text-sm wp:text-amber-900">
          {t("proxy.withdrawals.order.item_unavailable")}
        </p>
      ) : null}

      {checked && availableQuantity > 1 ? (
        <div className="wp:mt-4">
          <label htmlFor={`quantity-${safeId}`} className="wp:mb-2 wp:block wp:text-base wp:font-medium">
            {t("proxy.withdrawals.order.withdrawal_quantity_label")}
          </label>
          <select
            id={`quantity-${safeId}`}
            defaultValue={availableQuantity}
            {...register(`line_item_quantities.${fieldKey}`, { valueAsNumber: true })}
            className={inputClass}
          >
            {Array.from({ length: availableQuantity }, (_, index) => index + 1).map((quantity) => (
              <option key={quantity} value={quantity}>
                {quantity}
              </option>
            ))}
          </select>
        </div>
      ) : null}

      {checked && withdrawalReasons.length > 0 ? (
        <div className="wp:mt-4">
          <label htmlFor={`reason-${safeId}`} className="wp:mb-2 wp:block wp:text-base wp:font-medium">
            {t("proxy.withdrawals.order.item_reason_label")}
          </label>
          <select id={`reason-${safeId}`} {...register(`line_item_reason_ids.${fieldKey}`)} className={inputClass}>
            <option value="">{t("proxy.withdrawals.common.select_placeholder")}</option>
            {withdrawalReasons.map((candidate) => (
              <option key={candidate.id} value={candidate.id}>
                {t(`withdrawal_reasons.${candidate.key}`, { defaultValue: candidate.key })}
              </option>
            ))}
          </select>
        </div>
      ) : null}

      {checked && selectedReason?.key === "other" ? (
        <input
          className={`${inputClass} wp:mt-4`}
          {...register(`line_item_reason_details.${fieldKey}`)}
          placeholder={t("proxy.withdrawals.common.other_reason_placeholder")}
        />
      ) : null}
    </li>
  );
}
